import { DirectReportDto, TeamRagThresholds } from "@coaching-culture/types";
import { useGoalTerminology } from "auth/OrgProvider";
import { format, fromUnixTime } from "date-fns";
import {
  conversationsStatus,
  daysSinceFeedbackFromAnyone,
  daysSinceFeedbackFromMe,
  daysSinceLastConvo,
  daysToNextConvo,
  feedbackStatus,
  goalsBlocked,
  goalsSet,
  goalsStatus,
  measuresLastReviewed,
  measuresStatus,
  MetricStatus,
  nextConvo,
  overdueGoals,
} from "./metrics";

/** The drawers TeamViewModal can open on. */
export type TeamDrawer = "goals" | "conversation" | "feedback" | "measures";

export type NextStepTone = "danger" | "warning" | "calm";

export type NextStep = {
  tone: NextStepTone;
  tag: string;
  title: string;
  cta: string;
  /** Either opens the modal on a drawer, or links somewhere. */
  drawer?: TeamDrawer;
  href?: string;
};

type Terms = ReturnType<typeof useGoalTerminology>;

export type TeamCardStatuses = {
  goals: MetricStatus;
  conversations: MetricStatus;
  feedback: MetricStatus;
  measures: MetricStatus | null;
};

export const cardStatuses = (
  user: DirectReportDto,
  thresholds: TeamRagThresholds,
  hasMeasures: boolean,
): TeamCardStatuses => ({
  goals: goalsStatus(user, thresholds),
  conversations: conversationsStatus(user, thresholds),
  feedback: feedbackStatus(user, thresholds),
  measures: hasMeasures ? measuresStatus(user, thresholds) : null,
});

/** 2 for anything needing attention, 1 for at risk, 0 when all on track. */
export const worstStatusRank = (s: TeamCardStatuses) => {
  const all = [s.goals, s.conversations, s.feedback, s.measures];
  if (all.includes("danger")) return 2;
  if (all.includes("warning")) return 1;
  return 0;
};

export const firstName = (name: string) => name.trim().split(/\s+/)[0];

export const daysAgo = (n: number) =>
  n <= 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;

export const daysAhead = (n: number) =>
  n <= 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`;

const SUGGESTED = "Next step";

/**
 * Picks the one thing to show in a card's next-step strip. The worst problem
 * wins (needs attention before at risk; conversations, goals, feedback, then
 * measures). With nothing wrong it shows the next conversation, or a nudge
 * to give feedback, so the strip is never empty.
 */
export const nextStepFor = (
  user: DirectReportDto,
  myId: string,
  statuses: TeamCardStatuses,
  terms: Terms,
): NextStep => {
  const first = firstName(user.name);
  const goal = terms.goal.asBody();
  const goalTitle = terms.goal.asTitle();
  const next = nextConvo(user);
  const toNext = daysToNextConvo(user);
  const sinceLast = daysSinceLastConvo(user);
  const sinceFeedback = daysSinceFeedbackFromAnyone(user);
  const reviewed = measuresLastReviewed(user);

  const rules: Record<
    keyof TeamCardStatuses,
    (tone: "danger" | "warning") => NextStep | null
  > = {
    conversations: (tone) => {
      if (next == null) {
        return {
          tone,
          tag: SUGGESTED,
          title:
            sinceLast == null
              ? `Book a first conversation with ${first}`
              : `Book a conversation. The last was ${daysAgo(sinceLast)}.`,
          cta: "Schedule",
          drawer: "conversation",
        };
      }
      if (tone === "danger") {
        return {
          tone,
          tag: SUGGESTED,
          title: `The next conversation is ${daysAhead(toNext ?? 0)}. Book one sooner?`,
          cta: "Schedule",
          drawer: "conversation",
        };
      }
      return null;
    },
    goals: (tone) => ({
      tone,
      tag: SUGGESTED,
      title:
        goalsSet(user) === 0
          ? `Agree a ${goal} with ${first}`
          : `All of ${first}'s ${terms.goal.asPluralBody()} are overdue`,
      cta: `Set ${goalTitle}`,
      drawer: "goals",
    }),
    feedback: (tone) => ({
      tone,
      tag: SUGGESTED,
      title:
        sinceFeedback == null
          ? `${first} hasn't had any feedback yet`
          : `${first} last had feedback ${daysAgo(sinceFeedback)}`,
      cta: "Give feedback",
      drawer: "feedback",
    }),
    measures: (tone) => ({
      tone,
      tag: SUGGESTED,
      title:
        reviewed == null
          ? `Review ${first}'s measures for the first time`
          : `Measures were last reviewed ${format(fromUnixTime(reviewed), "d MMM")}`,
      cta: "Review",
      drawer: "measures",
    }),
  };

  const order: (keyof TeamCardStatuses)[] = [
    "conversations",
    "goals",
    "feedback",
    "measures",
  ];

  for (const tone of ["danger", "warning"] as const) {
    for (const area of order) {
      if (statuses[area] === tone) {
        const step = rules[area](tone);
        if (step) return step;
      }
    }
  }

  if (next != null) {
    return {
      tone: "calm",
      tag: "Coming up",
      title: `Conversation ${format(
        fromUnixTime(next.scheduledDate),
        "EEE d MMM, HH:mm",
      )}`,
      cta: "Prepare",
      href: `/solutions/performance/conversations/${next.id}/${next.index}`,
    };
  }

  const fromMe = daysSinceFeedbackFromMe(user, myId);
  return {
    tone: "calm",
    tag: "Keep in touch",
    title:
      fromMe == null
        ? `You haven't given ${first} feedback yet`
        : `Your last feedback to ${first} was ${daysAgo(fromMe)}`,
    cta: "Give feedback",
    drawer: "feedback",
  };
};

/** One line of plain-English detail per area, shared by the card and modal. */
export const areaDetails = (
  user: DirectReportDto,
  myId: string,
  terms: Terms,
) => {
  const set = goalsSet(user);
  const overdue = overdueGoals(user);
  const blocked = goalsBlocked(user);
  const goals =
    set === 0
      ? `No ${terms.goal.asPluralBody()} set`
      : [
          `${set} set`,
          overdue > 0 && `${overdue} overdue`,
          blocked > 0 && `${blocked} blocked`,
          overdue === 0 && blocked === 0 && "none overdue",
        ]
          .filter(Boolean)
          .join(" · ");

  const next = nextConvo(user);
  const sinceLast = daysSinceLastConvo(user);
  const conversations =
    next != null
      ? `Next ${format(fromUnixTime(next.scheduledDate), "EEE d MMM")} · ${daysAhead(
          daysToNextConvo(user) ?? 0,
        )}`
      : sinceLast != null
        ? `None booked · last ${daysAgo(sinceLast)}`
        : "None booked yet";

  const fromAnyone = daysSinceFeedbackFromAnyone(user);
  const fromMe = daysSinceFeedbackFromMe(user, myId);
  const feedback =
    fromAnyone == null
      ? "None received yet"
      : `Last received ${daysAgo(fromAnyone)}` +
        (fromMe == null
          ? " · none from you"
          : fromMe !== fromAnyone
            ? ` · yours ${daysAgo(fromMe)}`
            : " · from you");

  const reviewed = measuresLastReviewed(user);
  const measures =
    reviewed == null
      ? "Not reviewed yet"
      : `Last reviewed ${format(fromUnixTime(reviewed), "d MMM")}`;

  return { goals, conversations, feedback, measures };
};
